import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { NotificationCardHeader } from './NotificationCardHeader';
import { NotificationCardError } from './NotificationCardError';
import { NotificationHourSelect } from './NotificationHourSelect';
import { NotificationMessageEditor } from './NotificationMessageEditor';
import { NotificationResetFooter } from './NotificationResetFooter';
import { NotifyRoleToggles } from './NotifyRoleToggles';
import { QuarterlyEvalReminderNotifyRole } from '../../services/slackService';

/**
 * Selectable recipient roles, ordered from broadest to narrowest scope.
 * MEMBER ("Leaders") is accepted by the backend but temporarily not offered.
 */
export const QUARTERLY_EVAL_NOTIFY_ROLE_OPTIONS: QuarterlyEvalReminderNotifyRole[] = [
  QuarterlyEvalReminderNotifyRole.ADMIN,
  QuarterlyEvalReminderNotifyRole.MANAGER,
];

export interface QuarterlyEvalReminderSettingsDraft {
  notificationHour: number;
  notifyRoles: QuarterlyEvalReminderNotifyRole[];
}

/**
 * True when the reminder would reach nobody: the notification is DM-only, so a
 * draft without recipient roles is a silently dead reminder.
 */
export const hasNoQuarterlyEvalRecipients = (
  draft: QuarterlyEvalReminderSettingsDraft
): boolean => draft.notifyRoles.length === 0;

interface QuarterlyEvalReminderCardProps {
  /** Quarterly eval reminder configuration draft (org-wide settings). */
  draft: QuarterlyEvalReminderSettingsDraft;
  /** Editable notification message body. */
  body: string;
  /**
   * The QUARTERLY_EVAL_REMINDER template's `enabled` flag acts as a master
   * switch. When off, the reminder is not sent.
   */
  enabled: boolean;
  allowedParams: string[];
  /** Validation error for the message body. */
  bodyError?: string | null;
  isResetting?: boolean;
  onEnabledChange: (value: boolean) => void;
  onChange: (patch: Partial<QuarterlyEvalReminderSettingsDraft>) => void;
  onBodyChange: (value: string) => void;
  onReset: () => void;
}

/**
 * Single collapsible "Quarterly Eval Reminder" card unifying the
 * QUARTERLY_EVAL_REMINDER template body/enabled controls with the
 * organization-wide reminder settings (hour and recipient toggles).
 * Presentational — all state is owned by the containing page.
 */
export const QuarterlyEvalReminderCard: React.FC<QuarterlyEvalReminderCardProps> = ({
  draft,
  body,
  enabled,
  allowedParams,
  bodyError,
  isResetting,
  onEnabledChange,
  onChange,
  onBodyChange,
  onReset,
}) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);

  const noRecipients = hasNoQuarterlyEvalRecipients(draft);

  return (
    <Card data-testid="quarterly-eval-reminder-card">
      <NotificationCardHeader
        title={t('quarterlyEvalReminderSettings.title')}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((prev) => !prev)}
        enabled={enabled}
        onEnabledChange={onEnabledChange}
        testIdPrefix="quarterly-eval-reminder"
      />

      {expanded && (
        <>
          <CardContent className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {t('quarterlyEvalReminderSettings.description')}
            </p>

            <NotificationHourSelect
              value={draft.notificationHour}
              onChange={(notificationHour) => onChange({ notificationHour })}
              id="quarterly-eval-reminder-notification-hour"
              testId="quarterly-eval-reminder-notification-hour"
              label={t('quarterlyEvalReminderSettings.notificationHourLabel')}
              hint={t('quarterlyEvalReminderSettings.notificationHourHint')}
            />

            <div className="space-y-3">
              <div>
                <Label>{t('quarterlyEvalReminderSettings.notifyRolesLabel')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('quarterlyEvalReminderSettings.notifyRolesHint')}
                </p>
              </div>
              <NotifyRoleToggles
                options={QUARTERLY_EVAL_NOTIFY_ROLE_OPTIONS}
                selected={draft.notifyRoles}
                onChange={(notifyRoles) => onChange({ notifyRoles })}
                translationPrefix="quarterlyEvalReminderSettings.notifyRoles"
                testIdPrefix="quarterly-eval-reminder"
              />
              {noRecipients && (
                <NotificationCardError
                  message={t('quarterlyEvalReminderSettings.errors.noRecipients')}
                  testId="quarterly-eval-reminder-no-recipients-error"
                />
              )}
            </div>

            <NotificationMessageEditor
              id="quarterly-eval-reminder-body"
              testIdPrefix="quarterly-eval-reminder"
              body={body}
              onBodyChange={onBodyChange}
              allowedParams={allowedParams}
              error={bodyError}
            />
          </CardContent>
          <NotificationResetFooter
            onReset={onReset}
            isResetting={isResetting}
            testId="quarterly-eval-reminder-reset"
          />
        </>
      )}
    </Card>
  );
};
